/**
 * AuthContext - Global Authentication State Management
 * 
 * This context manages the authentication state for the application.
 * It handles:
 * - Storing our backend JWT (not Neon Auth's token)
 * - User info from our backend
 * - Login/logout flows
 * - Token refresh
 * - Role-based access control
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../lib/api';
import {
  ROLES,
  PERMISSIONS,
  hasPermission as checkHasPermission,
  canAccessRoute,
  isAdminRole,
  isUserRole,
  isDeveloperRole,
  getDashboardType,
  getAdminPageType,
  getNavigationItems,
} from '../config/permissions';
import { hasAccess as checkHasAccess } from '../utils/accessControl';

const AuthContext = createContext(null);

// Storage keys
const TOKEN_KEY = 'asset_track_token';
const REFRESH_TOKEN_KEY = 'asset_track_refresh_token';
const USER_KEY = 'asset_track_user';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const storedUser = localStorage.getItem(USER_KEY);

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          
          // Verify token is still valid by fetching user (includes features array)
          try {
            const userData = await api.get('/auth/me');
            // userData now includes: features, roleId, roleName, companyPlan
            setUser(userData);
            localStorage.setItem(USER_KEY, JSON.stringify(userData));
          } catch (err) {
            // Token invalid, try refresh
            const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
            if (refreshToken) {
              try {
                const { token: newToken } = await api.post('/auth/refresh', { refreshToken });
                setToken(newToken);
                localStorage.setItem(TOKEN_KEY, newToken);
                
                const userData = await api.get('/auth/me');
                setUser(userData);
                localStorage.setItem(USER_KEY, JSON.stringify(userData));
              } catch (refreshErr) {
                // Refresh failed, clear auth
                clearAuth();
              }
            } else {
              clearAuth();
            }
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        clearAuth();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Clear authentication data
  const clearAuth = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }, []);

  /**
   * Complete OAuth login with our backend
   * Called after Neon Auth returns user identity
   * 
   * @param {Object} neonAuthUser - User info from Neon Auth
   * @param {string} neonAuthUser.email - User's email
   * @param {string} neonAuthUser.provider - Auth provider (google/microsoft/email)
   * @param {string} neonAuthUser.providerId - Provider's user ID
   * @param {string} [neonAuthUser.name] - User's name (optional)
   */
  const completeOAuthLogin = useCallback(async (neonAuthUser) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post('/auth/complete-oauth', {
        email: neonAuthUser.email,
        provider: neonAuthUser.provider,
        providerId: neonAuthUser.providerId,
        name: neonAuthUser.name
      });

      if (response.success) {
        setToken(response.token);
        setUser(response.user);
        localStorage.setItem(TOKEN_KEY, response.token);
        localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
        localStorage.setItem(USER_KEY, JSON.stringify(response.user));
        return { success: true, user: response.user };
      }
    } catch (err) {
      console.error('OAuth completion error:', err);
      setError(err.message || 'Failed to complete login');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Email/password signup with company code
   */
  const signup = useCallback(async (signupData) => {
    try {
      setLoading(true);
      setError(null);

      // Support both old format (email, password, name) and new format (object)
      const payload = typeof signupData === 'string' 
        ? { email: signupData, password: arguments[1], name: arguments[2] }
        : {
            // Account info
            email: signupData.email,
            password: signupData.password,
            fullName: signupData.fullName,
            
            // Company code (required)
            companyCode: signupData.companyCode
          };

      const response = await api.post('/auth/signup', payload);

      if (response.success) {
        setToken(response.token);
        setUser(response.user);
        localStorage.setItem(TOKEN_KEY, response.token);
        localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
        localStorage.setItem(USER_KEY, JSON.stringify(response.user));
        return { success: true };
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.message || 'Failed to create account');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Email/password login
   */
  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post('/auth/login', { email, password });

      if (response.success) {
        setToken(response.token);
        setUser(response.user);
        localStorage.setItem(TOKEN_KEY, response.token);
        localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
        localStorage.setItem(USER_KEY, JSON.stringify(response.user));
        return { success: true, user: response.user };
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Invalid email or password');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      clearAuth();
    }
  }, [clearAuth]);

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = Boolean(token && user);

  /**
   * Check if user has specific role
   */
  const hasRole = useCallback((role) => {
    if (!user) return false;
    if (Array.isArray(role)) {
      return role.includes(user.role);
    }
    return user.role === role;
  }, [user]);

  /**
   * Get the normalized user role (lowercase)
   */
  const userRole = useMemo(() => {
    return user?.role?.toLowerCase() || ROLES.USER;
  }, [user?.role]);

  /**
   * Check if user has a specific permission
   * First checks database permissions, then falls back to hardcoded matrix
   */
  const hasPermission = useCallback((permission) => {
    // Developers always have all permissions
    if (user?.isDeveloper) return true;
    
    // Check database permissions first (from user.permissions object)
    if (user?.permissions && typeof user.permissions === 'object') {
      if (permission in user.permissions) {
        return user.permissions[permission] === true;
      }
    }
    
    // Fallback to hardcoded permission matrix
    return checkHasPermission(userRole, permission);
  }, [userRole, user]);

  /**
   * Check if user can access a specific route
   */
  const canAccess = useCallback((route) => {
    return canAccessRoute(userRole, route);
  }, [userRole]);

  /**
   * Role type checks
   */
  const isAdmin = useMemo(() => isAdminRole(userRole), [userRole]);
  const isUser = useMemo(() => isUserRole(userRole), [userRole]);
  const isDeveloper = useMemo(() => isDeveloperRole(userRole), [userRole]);

  /**
   * Get dashboard type for current user
   */
  const dashboardType = useMemo(() => getDashboardType(userRole), [userRole]);

  /**
   * Get admin page type for current user
   */
  const adminPageType = useMemo(() => getAdminPageType(userRole), [userRole]);

  /**
   * Get navigation items for current user
   */
  const navItems = useMemo(() => getNavigationItems(userRole), [userRole]);

  /**
   * Check if user has access to a feature (NEW access control system)
   * @param {string} featureKey - Feature identifier (e.g., 'asset_management')
   */
  const hasAccess = useCallback((featureKey) => {
    return checkHasAccess(user, featureKey);
  }, [user]);

  const value = {
    // User and auth state
    user,
    token,
    loading,
    error,
    isAuthenticated,
    
    // Role checking
    hasRole,
    userRole,
    
    // Permission checking (legacy)
    hasPermission,
    canAccess,
    
    // Feature-based access control (NEW)
    hasAccess,
    
    // Role type flags
    isAdmin,
    isUser,
    isDeveloper,
    
    // Page type helpers
    dashboardType,
    adminPageType,
    navItems,
    
    // Auth actions
    completeOAuthLogin,
    signup,
    login,
    logout,
    clearError: () => setError(null),
    
    // Export constants for convenience
    ROLES,
    PERMISSIONS,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;

